<Components.ImposterBar />

<!-- Tailwind CSS -->
{#if showAdminBar && Components.TopBar}
  <Components.TopBar {sidebarModes}/>
{/if}

<!-- No Tailwind CSS -->
{#if $store?.sidebar?.mode === sidebarModes.CONTENT_BLOCKS}
  <Components.ContentBlocks {sidebarModes}/>
{/if}

{#if Layout}
  <svelte:component this={Layout} node={render.layout} {render} cmsClasses={classes} />
{/if}

<Notifications notification={$notification} />
<BackgroundUploader files={$files}/>

{#if showCookieBanner && config}
  <div transition:fly|local={{ duration: 400, y: 100, delay: 200 }} class="cookie-banner cms-tw">
    <div class="container mx-auto">
      <div class="flex gap-6 flex-wrap lg:flex-nowrap justify-between items-center text-gray-500">
        <div class="cookie-banner__title text-2xl font-extrabold tracking-tighter">
          <h1>{config.cookieBannerTitle?.value || 'Cookies'}</h1>
        </div>

        <div class="cookie-banner__text text-lg text-gray-500">
          <p>
            {@html config.cookieBannerText?.value || 'We use cookies to monitor performance and usage of this website.'}
          </p>
        </div>

        <div class="cookie-banner__buttons flex justify-end self-center ml-auto gap-3 flex-grow">
          {#if config.privacyPolicyLink?.value}
            <button class="cookie-banner__button cookie-banner__button--accept w-max font-extrabold uppercase px-4 py-2 bg-gray-800" on:click={linkToPrivacy}>Read More</button>
          {/if}
          <button class="cookie-banner__button cookie-banner__button--accept w-max font-extrabold uppercase px-4 py-2 bg-green-600 hover:bg-green-700" on:click={() => setCookiesValue(true)}>{config.cookieBannerAccept?.value || 'Accept Cookies'}</button>
        </div>
      </div>
    </div>
  </div>
{/if}

<PluginEditModal
  bind:isOpen={$editPluginModalOpen}
  modalTitle={$editPluginModal?.instance?.editPlugin?.title || 'Edit'}
  tabs={$editPluginModal?.instance?.editPlugin?.tabs || []}
  on:tabChange={(e) => $editPluginModal.instance?.editPlugin?.tabChange(e.detail)}
  onSave={saveEditingPlugin}
  onClose={cancelEditingPlugin}
  pluginRef={$selectedPlugin?.ref}
  instance={$editPluginModal?.instance}
>
  <svelte:component
    this={$editPluginModal?.component}
    bind:this={$editPluginModal.instance}
    {...platform.api.core.template.pluginProps($store.render, { pluginId: $selectedPlugin.id })}
    isNew={$selectedPlugin?.isNew}
    pluginRef={$selectedPlugin?.ref}
  />
</PluginEditModal>

<script context="module">export const svelteCompilerVersion = {"major":4,"minor":2,"patch":18};
  export const Components = {
    load: [
      'core/extensions/top_bar/v1.0.0/ImposterBar',
      'core/extensions/content_blocks/v1.0.0/ContentBlocks',
      { ref: 'core/extensions/top_bar/v1.0.0/TopBar', condition: platform => platform.api.core.hasScope('admin') },
    ],
  }
</script>

<script>
  import PluginEditModal from '@/components/ui/plugin_edit_modal/PluginEditModal.svelte'
  import { getContext, onMount } from 'svelte'
  import { fly } from 'svelte/transition'
  import Notifications from '@/components/core/ui/general/Notifications/v1.0.0/Notifications.svelte'
  import BackgroundUploader from '@/components/core/ui/general/BackgroundUploader/v1.0.0/BackgroundUploader.svelte'

  const platform = getContext('platform')
  const { store } = platform
  const notification = platform.api.core.store('jolojo.notification')
  const files = platform.api.core.store('jolojo.files')

  const editPluginModalOpen = platform.api.core.store('editingPlugin.modalIsOpen') || false
  const editPluginModal = platform.api.core.store('editingPlugin.editModal') || {}
  const selectedPlugin = platform.api.core.store('editingPlugin.selectedPlugin') || {}

  let render = {}
  let showAdminBar = false
  let usesSidebar = false
  const showSidebar = platform.api.core.hasScope('super') || usesSidebar
  let classes = ''
  let showCookieBanner = false
  const config = null

  const sidebarModes = {
    CATEGORIES: 'categories',
    ADD_PLUGINS: 'addPlugins',
    NAMED_PLUGINS: 'namedPlugins',
    CONTENT_BLOCKS: 'contentBlocks',
  }

  $: Layout = platform.components[$store.render.template.custom.layout]?.default
  $: showAdminBar = $store.auth.claims && platform.api.core.hasScope('admin')

  $: {
    classes = [
      showAdminBar ? 'cms-admin-margin' : null,
      showSidebar ? 'cms-sidebar-margin' : null,
      $store.imposter ? 'cms-imposter-margin' : null,
      $store.showCMSMenu ? 'cms-menu-open' : null,
    ].filter(c => !!c).join(' ')

    render = platform.api.core.copyObject($store.render)

    if (IS_BROWSER) {
      document.title = $store.render.page.title

      onPageLoad()
    }
  }

  $: if (IS_BROWSER && Array.isArray($store.render.trackers)) {
    setupTrackers()
  }

  $: if (IS_BROWSER && $store.cookies) {
    setupTrackers()
  }

  function saveEditingPlugin () {
    platform.api.core.dispatch('save.editPlugin')
  }

  function cancelEditingPlugin () {
    console.log('cancelEditingPlugin')
    platform.api.core.dispatch('cancel.editPlugin')
  }

  onMount(async () => {
    const config = await platform.api.cms.config.findAllAsObject()

    if (config?.sidebar) {
      config.sidebar?.value === 'no' ? usesSidebar = false : usesSidebar = true
    }

    $files = []

    await import('@/styles/vars.css')
    await import('@/styles/jolojo-ui-styles.css')
  })

  function onPageLoad () {
    if (!IS_BROWSER) {
      return
    }

    setTimeout(() => {
      if (typeof window.cms.onPageLoad === 'function') {
        window.cms.onPageLoad()
      }
    }, 300)
  }

  function linkToPrivacy () {
    if (!config) { return }

    if (config.privacyPolicyLink.value) {
      platform.router.push(config.privacyPolicyLink.value)
    }
  }

  async function setupTrackers () {
    for (const tracker of $store.render.trackers) {
      if (tracker.loaded || tracker.type !== 'google-tag-manager') {
        continue
      }

      tracker.loaded = true

      const script = document.createElement('script')

      script.setAttribute('nonce', $store.nonce)

      script.innerText = [
        `window.cms.ready(function () {`,
        `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':`,
        `new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],`,
        `j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=`,
        `platform.api.core.unsafe.trustedScriptURL('https://www.googletagmanager.com/gtm.js?id='+i+dl);`,
        `f.parentNode.insertBefore(j,f);`,
        `})(window,document,'script','dataLayer','${tracker.fields.accountId}')`,
        `})`,
      ].join('')

      document.body.appendChild(script)
    }
  }

  function setCookiesValue (value) {
    $store.cookies = value

    window.localStorage.setItem('cookies', $store.cookies ? 'yes' : 'no')

    showCookieBanner = false
  }
</script>

<style>
  .cookie-banner {
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    padding: 20px;
    background: #fff;
    z-index: 100;
    box-shadow: 0px -5px 10px rgba(0,0,0,.07)
  }

  .cookie-banner__title h1 {
    font-size: 1.5em;
    margin: 0;
  }

  .cookie-banner__button {
    padding: 7px 10px;
    border: none;
    color: white;
    border-radius: 3px;
    cursor: pointer;
  }

  :global(.icon) {
    font-family: "Material Icons";
    height: 40px;
    width: 40px;
    font-size: 2em;
    background: none;
    border: none;
    transition: 0.3s ease-in-out;
    cursor: pointer;
    font-size: 25px;
  }

  :global(.icon.button) {
    font-family: 'Roboto';
    font-size: 1em;
    font-weight: 600;
    width: auto;
  }

  :global(.icon:hover) {
    background: #f8f8f8;
  }

  :global(.cms-mode--edit-plugin .cms-plugin),
  :global(.cms-mode--sort-plugin .cms-plugin),
  :global(.cms-mode--remove-plugin .cms-plugin) {
    min-height: 50px;
    min-width: 50px;
  }

  :global(.cms-plugin) {
    min-height: 20px;
  }

</style>
